// ============================================================================
// Occupancy metrics — site-night expansion, occupancy %, gap detection.
// A stay occupies each of its active sites for nights [checkIn, checkOut).
// ============================================================================

import { addDays } from '../dateUtils';
import { enumerateDays } from './aggregations';
import { AnalysisStay, isRevenueStay } from './types';

export interface OccupancyPoint {
  date: string;
  label: string;
  occupied: number;
  available: number;
  pct: number; // 0–100
}

interface SiteNightIndex {
  /** date → set of occupied siteIds */
  byDate: Map<string, Set<number>>;
  /** siteId → sorted list of occupied dates */
  bySite: Map<number, string[]>;
}

/** Expand revenue stays into per-date occupied-site sets, clipped to [from, to]. */
export function expandSiteNights(stays: AnalysisStay[], from: string, to: string): SiteNightIndex {
  const byDate = new Map<string, Set<number>>();
  const bySite = new Map<number, Set<string>>();

  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (stay.checkOut <= from || stay.checkIn > to) continue;
    const activeSites = stay.sites.filter(s => s.status === 'active');
    if (activeSites.length === 0) continue;
    let night = stay.checkIn > from ? stay.checkIn : from;
    while (night < stay.checkOut && night <= to) {
      let dateSet = byDate.get(night);
      if (!dateSet) {
        dateSet = new Set();
        byDate.set(night, dateSet);
      }
      for (const site of activeSites) {
        dateSet.add(site.siteId);
        let siteDates = bySite.get(site.siteId);
        if (!siteDates) {
          siteDates = new Set();
          bySite.set(site.siteId, siteDates);
        }
        siteDates.add(night);
      }
      night = addDays(night, 1);
    }
  }

  const bySiteSorted = new Map<number, string[]>();
  bySite.forEach((dates, siteId) => {
    bySiteSorted.set(siteId, Array.from(dates).sort());
  });
  return { byDate, bySite: bySiteSorted };
}

export interface BlackoutRange {
  siteId?: number; // undefined = whole store
  startDate: string;
  endDate: string; // EXCLUSIVE — the first night the site is bookable again
}

/**
 * date → set of blacked-out siteIds within [from, to].
 *
 * A blackout blocks nights [startDate, endDate), matching what the booking
 * engine enforces (`start_date < check_out AND end_date > check_in` in
 * hcp-backend/app/services/availability_service.py). Counting endDate itself
 * would mark one more unavailable night per blackout than guests actually see.
 */
export function blackoutSiteNightsByDate(
  blackouts: BlackoutRange[],
  allSiteIds: number[],
  from: string,
  to: string
): Map<string, Set<number>> {
  const byDate = new Map<string, Set<number>>();
  for (const blackout of blackouts) {
    let day = blackout.startDate > from ? blackout.startDate : from;
    while (day <= to && day < blackout.endDate) {
      let existing = byDate.get(day);
      if (!existing) {
        existing = new Set();
        byDate.set(day, existing);
      }
      const dateSet = existing;
      if (blackout.siteId !== undefined) dateSet.add(blackout.siteId);
      else allSiteIds.forEach(id => dateSet.add(id));
      day = addDays(day, 1);
    }
  }
  return byDate;
}

/** Daily occupancy % over [from, to]. Available = sites − blacked-out. */
export function occupancySeries(
  stays: AnalysisStay[],
  allSiteIds: number[],
  blackouts: BlackoutRange[],
  from: string,
  to: string
): OccupancyPoint[] {
  const { byDate } = expandSiteNights(stays, from, to);
  const blackoutByDate = blackoutSiteNightsByDate(blackouts, allSiteIds, from, to);
  return enumerateDays(from, to).map(date => {
    const blacked = blackoutByDate.get(date)?.size ?? 0;
    const available = Math.max(0, allSiteIds.length - blacked);
    const occupied = byDate.get(date)?.size ?? 0;
    const d = new Date(date + 'T12:00:00');
    return {
      date,
      label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      occupied,
      available,
      pct: available > 0 ? Math.round((occupied / available) * 1000) / 10 : 0,
    };
  });
}

/** Fri/Sat vs Sun–Thu average occupancy. */
export function weekendVsWeekday(series: OccupancyPoint[]): { weekend: number; weekday: number } {
  const weekend: number[] = [];
  const weekday: number[] = [];
  for (const point of series) {
    const day = new Date(point.date + 'T12:00:00').getDay();
    (day === 5 || day === 6 ? weekend : weekday).push(point.pct);
  }
  const avg = (xs: number[]) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : 0);
  return { weekend: avg(weekend), weekday: avg(weekday) };
}

// ============================================================================
// Gap nights — single unoccupied nights between two occupied nights on the
// same site. These are effectively unsellable and represent lost revenue.
// ============================================================================

export interface GapNight {
  siteId: number;
  date: string;
}

export function detectGapNights(stays: AnalysisStay[], from: string, to: string): GapNight[] {
  const { bySite } = expandSiteNights(stays, from, to);
  const gaps: GapNight[] = [];
  bySite.forEach((dates, siteId) => {
    for (let i = 0; i < dates.length - 1; i++) {
      // exactly one empty night between consecutive occupied nights
      if (addDays(dates[i], 2) === dates[i + 1]) {
        gaps.push({ siteId, date: addDays(dates[i], 1) });
      }
    }
  });
  return gaps.sort((a, b) => a.date.localeCompare(b.date));
}

// ============================================================================
// Arrival / departure day-of-week pattern
// ============================================================================

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface WeekdayCount {
  label: string;
  value: number;
}

export function countByWeekday(dates: string[]): WeekdayCount[] {
  const counts = new Array(7).fill(0);
  for (const date of dates) {
    counts[new Date(date + 'T12:00:00').getDay()] += 1;
  }
  return WEEKDAY_LABELS.map((label, i) => ({ label, value: counts[i] }));
}

export function arrivalsByWeekday(stays: AnalysisStay[], from: string, to: string): WeekdayCount[] {
  return countByWeekday(
    stays.filter(isRevenueStay).filter(s => s.checkIn >= from && s.checkIn <= to).map(s => s.checkIn)
  );
}

export function departuresByWeekday(stays: AnalysisStay[], from: string, to: string): WeekdayCount[] {
  return countByWeekday(
    stays.filter(isRevenueStay).filter(s => s.checkOut >= from && s.checkOut <= to).map(s => s.checkOut)
  );
}

// ============================================================================
// Site × week heatmap
// ============================================================================

export interface HeatmapCell {
  weekStart: string;
  value: number; // 0..1 share of the week's nights occupied
  blackout: boolean;
}

export interface HeatmapRow {
  siteId: number;
  label: string;
  cells: HeatmapCell[];
}

export function siteWeekHeatmap(
  stays: AnalysisStay[],
  sites: Array<{ id: number; alias: string }>,
  blackouts: BlackoutRange[],
  from: string,
  to: string,
  weekStarts: string[]
): HeatmapRow[] {
  const { bySite } = expandSiteNights(stays, from, to);
  const blackoutByDate = blackoutSiteNightsByDate(blackouts, sites.map(s => s.id), from, to);

  return sites.map(site => {
    const occupiedDates = new Set(bySite.get(site.id) ?? []);
    const cells = weekStarts.map(weekStartDate => {
      let occupied = 0;
      let inWindow = 0;
      let blackout = false;
      for (let i = 0; i < 7; i++) {
        const day = addDays(weekStartDate, i);
        if (day < from || day > to) continue;
        inWindow += 1;
        if (occupiedDates.has(day)) occupied += 1;
        if (blackoutByDate.get(day)?.has(site.id)) blackout = true;
      }
      return {
        weekStart: weekStartDate,
        value: inWindow > 0 ? occupied / inWindow : 0,
        blackout,
      };
    });
    return { siteId: site.id, label: site.alias, cells };
  });
}

/** Total blacked-out site-nights in the window (for the impact KPI). */
export function totalBlackoutSiteNights(
  blackouts: BlackoutRange[],
  allSiteIds: number[],
  from: string,
  to: string
): number {
  const byDate = blackoutSiteNightsByDate(blackouts, allSiteIds, from, to);
  let total = 0;
  byDate.forEach(set => {
    total += set.size;
  });
  return total;
}
